// Redux/Slices/Common/Payment/BookingPaymentSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Common/Interceptors/AxiosConfig";
import logger from "../../../../utils/logger";

const getErrorMessage = (message, fallback = "Something went wrong") => {
  if (!message) return fallback;
  if (typeof message === "string") return message;
  if (typeof message?.error === "object") {
    return (
      message.error.description ||
      message.error.message ||
      fallback
    );
  }
  if (typeof message?.message === "string") return message.message;
  if (typeof message?.description === "string") return message.description;
  return fallback;
};

const initialState = {
  isLoading: false,
  error: null,
  currentBooking: null,
  razorpayOrderId: null,
  paymentSuccess: false,
  bookingDetails: null,
};

const bookingPaymentSlice = createSlice({
  name: "bookingPayment",
  initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setCurrentBooking: (state, action) => {
      state.currentBooking = action.payload;
    },
    setRazorpayOrderId: (state, action) => {
      state.razorpayOrderId = action.payload;
    },
    setPaymentSuccess: (state, action) => {
      state.paymentSuccess = action.payload;
    },
    setBookingDetails: (state, action) => {
      state.bookingDetails = action.payload;
    },
    resetPayment: (state) => {
      state.isLoading = false;
      state.error = null;
      state.currentBooking = null;
      state.razorpayOrderId = null;
      state.paymentSuccess = false;
      state.bookingDetails = null;
    }
  }
});

// New: Create booking request (without payment) - provider must accept first
export const createBookingRequest = (bookingData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  logger.log(bookingData, "create booking request redux");
  console.log(bookingData, "booking data")
  try {
    const response = await axiosInstance.post('/client/booking/caring/request', bookingData);
    const { status, data, message } = response.data;
    if (status === "success") {
      logger.log("booking request created successfully");
      dispatch(setCurrentBooking(bookingData));
      dispatch(setBookingDetails(data));
      return { success: true, data };
    } else {
      const errorMsg = getErrorMessage(message, "Failed to create booking request");
      dispatch(setError(errorMsg));
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    logger.error('booking request creation failed', error);
    const errorResponse = error.response?.data || {};
    const errorMsg = getErrorMessage(errorResponse.message, 'failed to create booking request');
    dispatch(setError(errorMsg));
    // Include requiresWhatsApp flag if present
    return {
      success: false,
      error: errorMsg,
      requiresWhatsApp: errorResponse.requiresWhatsApp || false
    };

  } finally {
    dispatch(setLoading(false));
  }
};

export const createBookingOrder = (bookingData) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  logger.log(bookingData, "create booking order redux");
  try {
    const response = await axiosInstance.post('/bookings/payment/order', bookingData);
    const { status, data, message } = response.data;
    if (status === "success") {
      logger.log("order created successfully");
      dispatch(setCurrentBooking(bookingData));
      dispatch(setRazorpayOrderId(data?.razorpayOrderId));
      return { success: true, data };
    } else {
      const errorMsg = getErrorMessage(message, "failed to create order");
      dispatch(setError(errorMsg));
      return { success: false, error: errorMsg };
    }
  } catch (error) {
    logger.error('order creating failed', error);
    const errorResponse = error.response?.data || {};
    const errorMsg = getErrorMessage(
      errorResponse.message || errorResponse.error || error.message,
      'Failed to create order. Please try again.'
    );
    dispatch(setError(errorMsg));
    return { success: false, error: errorMsg };

  } finally {
    dispatch(setLoading(false));
  }
};

export const verifyPayment = (body) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const verificationData = {
      razorpay_order_id: body?.razorpay_order_id,
      razorpay_payment_id: body?.razorpay_payment_id,
      razorpay_signature: body?.razorpay_signature,
      serviceId: body?.serviceId,
      bookingId: body?.bookingId // ✅ FIX: Include bookingId if provided (for accepted bookings)
    };
    const response = await axiosInstance.post('/bookings/payment/verify', verificationData);

    const { status, data, message } = response.data;

    if (status === 'success') {
      dispatch(setPaymentSuccess(true));
      dispatch(setBookingDetails(data));
      return { success: true, data };
    } else {
      dispatch(setError(message));
      return { success: false, error: message };
    }
  } catch (error) {
    logger.error('❌ Verify payment error:', error.response?.data || error.message);
    const errorMsg = error.response?.data?.message || 'Payment verification failed';
    dispatch(setError(errorMsg));
    return { success: false, error: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
}

export const {
  setLoading,
  setError,
  setCurrentBooking,
  setRazorpayOrderId,
  setPaymentSuccess,
  setBookingDetails,
  resetPayment
} = bookingPaymentSlice.actions;

export default bookingPaymentSlice.reducer;
